import z from 'zod';

export enum GraphType {
  LINE = 'line',
  BAR = 'bar',
  SCATTER = 'scatter',
}

export interface GraphDataPoint {
  x: number;
  y: number;
}

export interface XAxisConfig {
  label: string;
  min?: number;
  max?: number;
}

export interface YAxisConfig extends XAxisConfig {
  data: Record<string, GraphDataPoint[]>;
}

export interface GraphConfig {
  graphType?: GraphType | string;
  x?: XAxisConfig;
  y1?: YAxisConfig;
  y2?: YAxisConfig;
}

export interface DataSeries {
  key: string;
  data: GraphDataPoint[];
  name: string;
  yAxisId: string;
}

export interface ParsedGraphConfig {
  chartType: GraphType;
  xAxis: XAxisConfig | null;
  yAxes: Array<{id: string; config: YAxisConfig}>;
  dataSeries: DataSeries[];
}

// Zod schema for GraphDataPoint
export const graphDataPointSchema = z.object({
  x: z.number(),
  y: z.number(),
});

// Zod schema for XAxisConfig
export const xAxisConfigSchema = z.object({
  label: z.string(),
  min: z.number().optional(),
  max: z.number().optional(),
});

// Zod schema for YAxisConfig
export const yAxisConfigSchema = xAxisConfigSchema.extend({
  data: z.record(z.string(), z.array(graphDataPointSchema)),
});

// Zod schema for GraphConfig
export const graphConfigSchema = z
  .object({
    graphType: z.string().optional(),
    x: xAxisConfigSchema.optional(),
    y1: yAxisConfigSchema.optional(),
    y2: yAxisConfigSchema.optional(),
  })
  .superRefine((value, ctx) => {
    // If graphType is provided but not recognised, treat as "not a bond graph".
    if (value.graphType) {
      const allowedGraphTypes = Object.values(GraphType);
      if (!allowedGraphTypes.includes(value.graphType as GraphType)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['graphType'],
          message: `Unsupported graphType "${value.graphType}"`,
        });
      }
    }

    // Require at least one Y axis configuration for bond graphs
    if (!value.y1 && !value.y2) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'At least one of y1 or y2 is required',
      });
    }
  }) satisfies z.ZodType<GraphConfig, any, any>;
